import React from 'react';
import PropTypes from 'prop-types';

import {Box, CardContent, Divider, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {CallStatusDropdown, StageDropdown} from './dropdowns';

const CardBody = (props) => {
  const {
    callStatus,
    clientCreatedAtLabel,
    clientDaysInStageText,
    clientDaysSinceLastContactText,
    clientLastMessage,
    clientLocation,
    clientName,
    footer,
    hasUnreadMessage,
    leadQuality,
    notSetLabel,
    onCallStatusOverlayChange,
    onStatusOverlayChange,
    primaryRelationship,
    selectedCallStatus,
    selectedStage,
    setSelectedCallStatus,
    setSelectedStage,
  } = props;

  return (
    <CardContent
      sx={{
        p: '20px !important',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Stack
        sx={{
          height: '100%',
          justifyContent: 'space-between',
          minWidth: 0,
        }}
      >
        <Stack spacing={1.5} sx={{minWidth: 0}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {
                  hasUnreadMessage &&
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#DA7955',
                      flexShrink: 0,
                    }}
                  />
                }
                <Typography
                  component='div'
                  sx={{
                    color: '#1A1916',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '21px',
                    minWidth: 0,
                  }}
                >
                  {clientName}
                </Typography>
              </Box>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  mt: '4px',
                  wordBreak: 'break-word',
                }}
              >
                {primaryRelationship?.name || notSetLabel}
              </Typography>
              {
                primaryRelationship?.connectionType &&
                <Typography
                  component='div'
                  sx={{
                    color: '#A8A49E',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    wordBreak: 'break-word',
                  }}
                >
                  ({primaryRelationship.connectionType})
                </Typography>
              }
            </Box>
            <Stack
              spacing={0.75}
              sx={{
                alignItems: 'flex-end',
                flexShrink: 0,
              }}
            >
              <StageDropdown
                onOverlayChange={onStatusOverlayChange}
                selectedStage={selectedStage}
                setSelectedStage={setSelectedStage}
                leadQuality={leadQuality}
              />
              {
                callStatus &&
                <CallStatusDropdown
                  onOverlayChange={onCallStatusOverlayChange}
                  selectedStatus={selectedCallStatus}
                  setSelectedStatus={setSelectedCallStatus}
                />
              }
            </Stack>
          </Box>

          <Divider sx={{borderColor: '#EBEBEB'}} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              columnGap: 2,
              rowGap: 2,
            }}
          >
            <Box>
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.DaysInStage' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                }}
              >
                <Translate
                  expression='clientLead.ClientLeadList.Card.Subtitles.InStage'
                  substitute={{value: clientDaysInStageText}}
                />
              </Typography>
              {
                clientLastMessage &&
                <Typography
                  component='div'
                  sx={{
                    color: '#1A1916',
                    fontSize: '11px',
                    fontWeight: 400,
                    lineHeight: '14px',
                    mt: '4px',
                  }}
                >
                  <Translate
                    expression='clientLead.ClientLeadList.Card.Subtitles.SinceLastMsg'
                    substitute={{value: clientDaysSinceLastContactText}}
                  />
                </Typography>
              }
            </Box>

            <Box
              sx={{
                ml: {
                  xs: 0, md: 4, lg: 8,
                },
              }}
            >
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.Location' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: clientLocation === notSetLabel ? '#A8A49E' : '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                  wordBreak: 'break-word',
                }}
              >
                {clientLocation}
              </Typography>
            </Box>

            <Box>
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.Created' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                }}
              >
                {clientCreatedAtLabel}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{borderColor: '#EBEBEB'}} />
          {footer}
        </Stack>
      </Stack>
    </CardContent>
  );
};

CardBody.propTypes = {
  callStatus: PropTypes.string,
  clientCreatedAtLabel: PropTypes.string.isRequired,
  clientDaysInStageText: PropTypes.string.isRequired,
  clientDaysSinceLastContactText: PropTypes.string,
  clientLastMessage: PropTypes.object,
  clientLocation: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  footer: PropTypes.node.isRequired,
  hasUnreadMessage: PropTypes.bool.isRequired,
  leadQuality: PropTypes.string,
  notSetLabel: PropTypes.string.isRequired,
  onCallStatusOverlayChange: PropTypes.func.isRequired,
  onStatusOverlayChange: PropTypes.func.isRequired,
  primaryRelationship: PropTypes.object,
  selectedCallStatus: PropTypes.string,
  selectedStage: PropTypes.string,
  setSelectedCallStatus: PropTypes.func.isRequired,
  setSelectedStage: PropTypes.func.isRequired,
};

export default React.memo(CardBody);
